import React from 'react';

interface SpotPrice {
	month_year: string;
	month_label: string;
	cee_classique: number | null;
	cee_precarite: number | null;
}

interface QuarterInfo {
	year: number;
	quarter: number;
}

interface QuarterData {
	info: QuarterInfo;
	months: string[];
	prices: SpotPrice[];
}

interface CeeQuarterlyTableProps {
	data: {
		current: QuarterData;
		previous: QuarterData;
	};
}

const formatPrice = (price: number | null): string => {
	if (price === null) return '-';
	return price.toFixed(2).replace('.', ',');
};

const getQuarterLabel = (info: QuarterInfo): string => {
	const ordinals = ['1er', '2e', '3e', '4e'];
	return `${ordinals[info.quarter - 1]} trimestre ${info.year}`;
};

const CalendarIcon: React.FC = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
		<line x1="16" y1="2" x2="16" y2="6"></line>
		<line x1="8" y1="2" x2="8" y2="6"></line>
		<line x1="3" y1="10" x2="21" y2="10"></line>
	</svg>
);

interface PriceDisplayProps {
	price: number | null;
	type: 'classique' | 'precarite';
}

const PriceDisplay: React.FC<PriceDisplayProps> = ({ price, type }) => {
	if (price === null) {
		return (
			<span className="gext-cee-quarterly-table__price gext-cee-quarterly-table__price--empty">
				-
			</span>
		);
	}
	return (
		<span
			className={`gext-cee-quarterly-table__price gext-cee-quarterly-table__price--${type}`}
		>
			{formatPrice(price)}
		</span>
	);
};

interface QuarterTableProps {
	quarter: QuarterData;
}

const QuarterTable: React.FC<QuarterTableProps> = ({ quarter }) => {
	return (
		<div className="gext-cee-quarterly-table__card">
			<div className="gext-cee-quarterly-table__header">
				<div className="gext-cee-quarterly-table__header-icon">
					<CalendarIcon />
				</div>
				<h3 className="gext-cee-quarterly-table__title">
					{getQuarterLabel(quarter.info)}
				</h3>
			</div>
			<table className="gext-cee-quarterly-table__table">
				<thead>
					<tr>
						<th>Mois</th>
						<th>
							Classique
							<span className="gext-cee-quarterly-table__unit">
								€/MWhc
							</span>
						</th>
						<th>
							Précarité
							<span className="gext-cee-quarterly-table__unit">
								€/MWhc
							</span>
						</th>
					</tr>
				</thead>
				<tbody>
					{quarter.prices.length > 0 ? (
						quarter.prices.map((row, index) => (
							<tr key={index}>
								<td>{row.month_label}</td>
								<td>
									<PriceDisplay
										price={row.cee_classique}
										type="classique"
									/>
								</td>
								<td>
									<PriceDisplay
										price={row.cee_precarite}
										type="precarite"
									/>
								</td>
							</tr>
						))
					) : (
						<tr>
							<td colSpan={3} className="gext-cee-quarterly-table__empty">
								Aucune donnée disponible
							</td>
						</tr>
					)}
				</tbody>
			</table>
			<p className="gext-cee-quarterly-table__caption">
				<em>Moyenne des prix de clôture CEE SPOT</em>
			</p>
		</div>
	);
};

const CeeQuarterlyTable: React.FC<CeeQuarterlyTableProps> = ({ data }) => {
	if (!data || !data.current || !data.previous) {
		return (
			<div className="gext-cee-quarterly-table gext-cee-quarterly-table--empty">
				<p>Aucune donnée disponible</p>
			</div>
		);
	}

	return (
		<div className="gext-cee-quarterly-table">
			<div className="gext-cee-quarterly-table__grid">
				<QuarterTable quarter={data.current} />
				<QuarterTable quarter={data.previous} />
			</div>
		</div>
	);
};

export default CeeQuarterlyTable;
